// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import ProductDescription, { ProductDescriptionStyles } from "@bg/Overrides/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@bg/Overrides/Components/ProductPartNumbers";
import RfqQuoteDetailsQuoteLineCalculatorRow from "@bg/Overrides/Widgets/RfqQuoteDetails/RfqQuoteDetailsQuoteLineCalculatorRow";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { maxLengthAttr } from "@bg/XDL/Utilities/inputValidation";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addPriceBreak from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/AddPriceBreak";
import resetPriceBreaks from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/ResetPriceBreaks";
import updateQuoteLine from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/UpdateQuoteLine";
import validatePriceBreaks from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/ValidatePriceBreaks";
import translate from "@insite/client-framework/Translate";
import { BreakPriceRfqModel, QuoteLineModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    quote: QuoteModel;
    quoteLine: QuoteLineModel;
    onCancel?: () => void;
    onApply?: () => void;
    isFixedQty: boolean;
    minQtyValue: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    priceBreakValidations: state.pages.rfqQuoteDetails.priceBreakValidations,
    isApplingQuote: state.pages.rfqQuoteDetails.isApplingQuote,
});

const mapDispatchToProps = {
    addPriceBreak,
    resetPriceBreaks,
    validatePriceBreaks: makeHandlerChainAwaitable(validatePriceBreaks),
    updateQuoteLine,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqQuoteDetailsQuoteLineCalculatorStyles {
    container?: InjectableCss;
    infoGridItem?: InjectableCss;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    itemPricingHeader?: TypographyPresentationProps;
    itemPricingContainer?: InjectableCss;
    unitCostGridItem?: InjectableCss;
    unitCostHeadingAndText?: SmallHeadingAndTextStyles;
    listPriceGridItem?: InjectableCss;
    listPriceHeadingAndText?: SmallHeadingAndTextStyles;
    customerPriceGridItem?: InjectableCss;
    customerPriceHeadingAndText?: SmallHeadingAndTextStyles;
    minimumPriceAllowedGridItem?: InjectableCss;
    minimumPriceAllowedHeadingAndText?: SmallHeadingAndTextStyles;
    qtyAvailableGridItem?: InjectableCss;
    qtyAvailableHeadingAndText?: SmallHeadingAndTextStyles;
    priceBreaksGridItem?: InjectableCss;
    fixedAndMinGrid?: InjectableCss;
    fixedQtyBox?: CheckboxPresentationProps;
    minQtyField?: TextFieldPresentationProps;
    priceBreaksTable?: InjectableCss;
    priceBreaksHead?: InjectableCss;
    qtyHeader?: InjectableCss;
    priceHeader?: InjectableCss;
    buttonsHeader?: InjectableCss;
    calculatorHeader?: InjectableCss;
    priceBreaksBody?: InjectableCss;
    addPriceBreakLink?: LinkPresentationProps;
    errorText?: TypographyPresentationProps;
    buttonsGridItem?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    resetButton?: ButtonPresentationProps;
    applyButton?: ButtonPresentationProps;
}

export const rfqQuoteDetailsQuoteLineCalculatorStyles: RfqQuoteDetailsQuoteLineCalculatorStyles = {
    container: {
        css: css`
            display: flex;
            @media (max-width: 900px) {
                flex-wrap: wrap;
            }
        `,
    },
    infoGridItem: {
        css: css`
            background: ${getColor("common.ui4")};
            padding: 25px 20px 20px;
            max-width: 540px;
            width: 100%;
            margin-right: 40px;
            @media (max-width: 900px) {
                max-width: 100%;
                margin-right: 0;
                margin-bottom: 30px;
            }
        `,
    },
    productDescription: {
        productDetailLink: {
            css: css`
                opacity: 0.7;
                margin-bottom: 12px;
            `,
        },
    },
    productPartNumbers: {
        manufacturerItemGridItem: {
            css: css`
                flex: 1;
                line-height: ${fontConfig.lineHeight.singleLine};
                font-size: ${fontConfig.fontSize.regular}px;
                height: auto;
                && > * {
                    display: block;
                    line-height: ${fontConfig.lineHeight.singleLine};
                    font-size: ${fontConfig.fontSize.regular}px;
                }
            `,
        },
        erpNumberGridItem: {
            css: css`
                flex: 1;
                line-height: ${fontConfig.lineHeight.singleLine};
                font-size: ${fontConfig.fontSize.regular}px;
                height: auto;
                && > * {
                    display: block;
                    line-height: ${fontConfig.lineHeight.singleLine};
                    font-size: ${fontConfig.fontSize.regular}px;
                }
            `,
        },
    },
    itemPricingHeader: {
        size: "22px",
        lineHeight: "22px",
        css: css`
            font-weight: 500;
            margin-top: 40px;
            margin-bottom: 15px;
            display: block;
        `,
    },
    itemPricingContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
        `,
    },
    unitCostGridItem: {
        css: css`
            width: calc(100% / 3);
        `,
    },
    unitCostHeadingAndText: {
        heading: {
            size: fontConfig.fontSize.regular,
            lineHeight: fontConfig.lineHeight.singleLine,
            weight: fontConfig.weight.semiBold,
        },
    },
    qtyAvailableHeadingAndText: {
        heading: {
            size: fontConfig.fontSize.regular,
            lineHeight: fontConfig.lineHeight.singleLine,
            weight: fontConfig.weight.semiBold,
            css: css`
                margin-top: 20px;
            `,
        },
    },
    buttonsGridItem: {
        css: css`
            &&&.modal-actions button + button {
                margin-right: 20px;
            }
        `,
    },
    priceBreaksGridItem: {
        css: css`
            flex-grow: 1;
            padding-bottom: 10px;
            border-bottom: 1px solid ${getColor("common.ui2")};
        `,
    },
    addPriceBreakLink: linkIconStyleGenerator(
        "AddToList",
        "13px",
        14,
        "font-size: 15px; line-height: 20px; font-weight: 500; opacity: 0.7;",
        "",
        "margin-top: 10px",
    ),
    fixedAndMinGrid: {
        css: css`
            display: flex;
            align-items: center;
            padding-top: 20px;
            border-top: 1px solid ${getColor("common.ui2")};
        `,
    },
    fixedQtyBox: {
        mergeCss: true,
        css: css`
            margin-right: 30px;
            label {
                white-space: nowrap;
            }
        `,
    },
    minQtyField: {
        cssOverrides: {
            formField: css`
                flex-direction: row;
                align-items: center;
                label {
                    width: auto;
                    font-weight: 500;
                    margin-right: 8px;
                    white-space: nowrap;
                    font-size: 14px;
                }
                input {
                    height: 32px;
                    text-align: center;
                    max-width: 50px;
                }
            `,
            formInputWrapper: css`
                margin-top: 0 !important;
            `,
        },
    },
    priceBreaksHead: {
        css: css`
            display: flex;
            padding: 10px 15px;
            margin: 20px 0 15px;
            background: ${getColor("common.ui4")};
        `,
    },
    qtyHeader: {
        css: css`
            font-size: ${fontConfig.fontSize.xSmall}px;
            line-height: ${fontConfig.lineHeight.small};
            font-weight: ${fontConfig.weight.semiBold};
            flex-grow: 1;
            text-transform: uppercase;
        `,
    },
    priceHeader: {
        css: css`
            font-size: ${fontConfig.fontSize.xSmall}px;
            line-height: ${fontConfig.lineHeight.small};
            font-weight: ${fontConfig.weight.semiBold};
            width: 140px;
            text-align: center;
            text-transform: uppercase;
        `,
    },
    resetButton: {
        css: css`
            color: ${getColor("secondary")};
            @media (max-width: 481px) {
                text-align: center;
                margin: 0 !important;
                margin-top: 10px;
            }
        `,
        typographyProps: {
            color: "secondary",
            css: css`
                @media (max-width: 481px) {
                    width: 100%;
                    text-align: center;
                }
            `,
        },
    },
    errorText: {
        color: "danger",
        css: css`
            display: block;
        `,
    },
    cancelButton: {
        css: css`
            @media (max-width: 481px) {
                margin-right: 0 !important;
            }
        `,
    },
};

const styles = rfqQuoteDetailsQuoteLineCalculatorStyles;

const MAX_PRICE_BREAKS = 5;

const regexp = /^[0-9\b]+$/;

const isValidNumberic = (number: string) => {
    if (number === "") {
        // backspace or delete case
        return true;
    }
    return regexp.test(number);
};

const RfqQuoteDetailsQuoteLineCalculator = ({
    quote,
    quoteLine,
    priceBreakValidations,
    isApplingQuote,
    onCancel,
    onApply,
    addPriceBreak,
    resetPriceBreaks,
    validatePriceBreaks,
    updateQuoteLine,
    isFixedQty,
    minQtyValue,
}: Props) => {
    const [priceBreakCalculatorIndex, setPriceBreakCalculatorIndex] = useState<number | undefined>(undefined);
    const [fixedQty, setFixedQty] = useState(isFixedQty);
    const [minQty, setMinQty] = useState(minQtyValue);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setFixedQty(isFixedQty);
    }, [isFixedQty]);

    useEffect(() => {
        const startQty = quoteLine?.pricingRfq?.priceBreaks?.[0].startQty;
        const minQty = !minQtyValue || minQtyValue === "0" ? startQty?.toString() || "1" : minQtyValue;
        setMinQty(minQty);
    }, [minQtyValue]);

    const shouldShowPriceBreak = (priceBreak: BreakPriceRfqModel) => {
        if (!quote || !quoteLine) {
            return false;
        }

        if (quote.isJobQuote) {
            return (
                quoteLine.pricingRfq?.priceBreaks
                    ?.slice()
                    .sort((a, b) => b.startQty - a.startQty)
                    .filter(x => x.startQty <= quoteLine.qtyOrdered!)[0].startQty === priceBreak.startQty
            );
        }

        return true;
    };

    const setFixedQtyHandler: CheckboxProps["onChange"] = (_, value) => {
        setFixedQty(value);
    };

    const minQtyChangeHandler = (e: any) => {
        if (isValidNumberic(e.target.value)) {
            setMinQty(e.target.value);
        }
    };

    const addPriceBreakClickHandler = () => {
        addPriceBreak({ isFixedQty: fixedQty, minQty } as any);
    };

    const openCalculator = (index: number) => {
        setPriceBreakCalculatorIndex(index);
    };

    const cancelClickHandler = () => {
        onCancel?.();
    };

    const resetClickHandler = () => {
        setFixedQty(isFixedQty);
        const startQty = quoteLine?.pricingRfq?.priceBreaks?.[0].startQty;
        const minQty = !minQtyValue || minQtyValue === "0" ? startQty?.toString() || "1" : minQtyValue;

        setMinQty(minQty);
        resetPriceBreaks({ minQty, isFixedQty } as any);
    };

    const applyClickHandler = async () => {
        if (!quote || !quoteLine) {
            return;
        }
        setIsLoading(true);
        const isValid = await validatePriceBreaks({ isFixedQty: fixedQty, minQty } as any);
        if (!isValid) {
            return;
        }

        updateQuoteLine({
            quoteId: quote.id,
            quoteLineId: quoteLine.id,
            isFixedQty: fixedQty,
            minimumQty: minQty,
            updatePriceBreakInfo: true,
            quoteLine: {
                id: quoteLine.id,
                pricingRfq: fixedQty
                    ? {
                          ...quoteLine.pricingRfq,
                          priceBreaks: [
                              {
                                  ...quoteLine.pricingRfq?.priceBreaks?.[0],
                                  endQty: quoteLine.pricingRfq?.priceBreaks?.[0].startQty,
                              },
                          ],
                      }
                    : quoteLine.pricingRfq,
            } as QuoteLineModel,
            onSuccess: onApply,
            onComplete(resultProps: any) {
                if (resultProps.result?.quoteLine) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                    setIsLoading(false);
                }
            },
        } as any);
    };

    return (
        <LoadingOverlay loading={isLoading}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.infoGridItem}>
                    <Typography variant="h6" forwardAs="span">
                        {translate("Item Description")}
                    </Typography>
                    <StyledWrapper>
                        <ProductDescription product={quoteLine} extendedStyles={styles.productDescription} />
                    </StyledWrapper>
                    <ProductPartNumbers
                        productNumber={quoteLine.properties.sku || quoteLine.erpNumber}
                        customerProductNumber={quoteLine.customerName}
                        manufacturerItem={quoteLine.manufacturerItem}
                        extendedStyles={styles.productPartNumbers}
                    />

                    <>
                        <Typography {...styles.itemPricingHeader}>{translate("Item Pricing")}</Typography>
                        <StyledWrapper {...styles.itemPricingContainer}>
                            {quoteLine.pricingRfq?.showUnitCost && (
                                <StyledWrapper {...styles.unitCostGridItem}>
                                    <SmallHeadingAndText
                                        heading={translate("Unit Cost")}
                                        text={quoteLine.pricingRfq.unitCostDisplay}
                                        extendedStyles={styles.unitCostHeadingAndText}
                                    />
                                </StyledWrapper>
                            )}
                            {quoteLine.pricingRfq?.showListPrice && (
                                <StyledWrapper {...styles.unitCostGridItem}>
                                    <SmallHeadingAndText
                                        heading={translate("List")}
                                        text={quoteLine.pricingRfq.listPriceDisplay}
                                        extendedStyles={styles.unitCostHeadingAndText}
                                    />
                                </StyledWrapper>
                            )}
                            {quoteLine.pricingRfq?.showCustomerPrice && (
                                <StyledWrapper {...styles.unitCostGridItem}>
                                    <SmallHeadingAndText
                                        heading={translate("Customer")}
                                        text={
                                            quote.isJobQuote
                                                ? quoteLine.pricing?.unitNetPriceDisplay || ""
                                                : quoteLine.pricingRfq.customerPriceDisplay
                                        }
                                        extendedStyles={styles.unitCostHeadingAndText}
                                    />
                                </StyledWrapper>
                            )}
                            {quoteLine.pricingRfq && quoteLine.pricingRfq.minimumPriceAllowed > 0 && (
                                <StyledWrapper {...styles.unitCostGridItem}>
                                    <SmallHeadingAndText
                                        heading={translate("Minimum")}
                                        text={quoteLine.pricingRfq.minimumPriceAllowedDisplay}
                                        extendedStyles={styles.unitCostHeadingAndText}
                                    />
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </>

                    <StyledWrapper {...styles.qtyAvailableGridItem}>
                        <SmallHeadingAndText
                            heading={translate("Qty Available in Default Branch")}
                            text={quoteLine.qtyOnHand}
                            extendedStyles={styles.qtyAvailableHeadingAndText}
                        />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.priceBreaksGridItem} data-test-selector="rfqConfirmation_quotedPricing">
                    <StyledWrapper {...styles.priceBreaksTable}>
                        <StyledWrapper {...styles.fixedAndMinGrid}>
                            <Checkbox onChange={setFixedQtyHandler} checked={fixedQty} {...styles.fixedQtyBox}>
                                {translate("Fixed Quantity")}
                            </Checkbox>
                            <TextField
                                value={minQty}
                                maxLength={maxLengthAttr.qty}
                                onChange={minQtyChangeHandler}
                                {...styles.minQtyField}
                                label={translate("Minimum Qty")}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.priceBreaksHead}>
                            <StyledWrapper {...styles.qtyHeader}>{translate("Qty")}</StyledWrapper>
                            <StyledWrapper {...styles.priceHeader}>{translate("Price")}</StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.priceBreaksBody} data-test-selector="quoteModal_price">
                            {quoteLine.pricingRfq?.priceBreaks
                                ?.filter(shouldShowPriceBreak)
                                .map((priceBreak, index) => (
                                    <RfqQuoteDetailsQuoteLineCalculatorRow
                                        key={index}
                                        quote={quote}
                                        quoteLine={quoteLine}
                                        priceBreak={priceBreak}
                                        index={index}
                                        isFixedQty={fixedQty}
                                        minQty={minQty}
                                        calculatorIsOpen={priceBreakCalculatorIndex === index}
                                        openCalculator={openCalculator}
                                    />
                                ))}
                        </StyledWrapper>
                    </StyledWrapper>
                    {!quote.isJobQuote &&
                        !fixedQty &&
                        quoteLine.pricingRfq?.priceBreaks &&
                        quoteLine.pricingRfq.priceBreaks.length < MAX_PRICE_BREAKS && (
                            <Link
                                {...styles.addPriceBreakLink}
                                onClick={addPriceBreakClickHandler}
                                data-test-selector="quoteCaculator_quoteModal_addPriceBreak"
                            >
                                {translate("Add Price Break")}
                            </Link>
                        )}
                    {priceBreakValidations.some(o => o.priceRequired) && (
                        <Typography {...styles.errorText} data-test-selector="price">
                            {siteMessage("Field_Required", translate("Price"))}
                        </Typography>
                    )}
                    {priceBreakValidations.some(o => o.invalidPrice) && (
                        <Typography {...styles.errorText}>{siteMessage("Rfq_InvalidLinePrice")}</Typography>
                    )}
                    {priceBreakValidations.some(o => o.invalidQty) && !fixedQty && (
                        <Typography {...styles.errorText}>{siteMessage("Rfq_InvalidQty")}</Typography>
                    )}
                    {fixedQty && (
                        <Typography
                            color="info"
                            css={css`
                                display: block;
                            `}
                        >
                            {siteMessage("Custom_UseMinQtyToUpdateValue")}
                        </Typography>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.buttonsGridItem} className="modal-actions">
                <Button
                    {...styles.applyButton}
                    disabled={isApplingQuote}
                    onClick={applyClickHandler}
                    data-test-selector="quoteModal_ApplyQuote"
                >
                    {translate("Apply Quote")}
                </Button>
                <Button {...styles.cancelButton} variant="secondary" onClick={cancelClickHandler}>
                    {translate("Cancel")}
                </Button>
                <Link {...styles.resetButton} onClick={resetClickHandler} hoverMode={undefined}>
                    {translate("Reset Table")}
                </Link>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RfqQuoteDetailsQuoteLineCalculator);
